import { HttpErrorResponse } from '@angular/common/http';

import type { Attachment } from '@recruitee/attachments-types';
import {
  createEntityCollectionStore,
  DEFAULT_SCOPE_NAME,
  getEntityCollectionActions,
  ngrxEvent,
  pipeState,
} from '@recruitee/ngrx';

import { getAttachmentStoreScope } from '../helpers/store-helper';
import { AttachmentsDataScope, EntityStoreKey } from '../types';

const { actions, selectors, reducer, namespace } = createEntityCollectionStore<Attachment>()(
  EntityStoreKey.attachments,
  {
    // eslint-disable-next-line max-lines-per-function
    customActions: adapter => {
      const actions = getEntityCollectionActions(adapter, ({ id }) => id!);
      return {
        fetchAttachmentsByCandidate: (
          state,
          payload: { params: { candidateId: number }; scope?: string; replace?: boolean },
        ) =>
          pipeState(
            state,
            next => (payload.replace ? adapter.resetAll(next) : next),
            next =>
              adapter.markAsPending(next, {
                scope: payload.scope || DEFAULT_SCOPE_NAME,
                entities: [],
              }),
          ),
        fetchAttachmentsByCandidateSuccess: (
          state,
          payload: { data: Attachment[]; scope?: string; replace?: boolean },
        ) =>
          pipeState(
            state,
            next => (payload.replace ? adapter.setMany(next, [], { scope: payload.scope }) : next),
            next => adapter.addMany(next, payload.data, { scope: payload.scope }),
            next =>
              adapter.markAsLoaded(next, {
                scope: payload.scope || DEFAULT_SCOPE_NAME,
                entities: payload.data.map(e => e.id),
              }),
          ),
        fetchAttachmentsByCandidateFailed: (state, payload: { error: HttpErrorResponse }) =>
          adapter.setError(state, payload.error),
        fetchAttachmentsByOffer: (
          state,
          payload: { params: { offerId: number }; scope?: string; replace?: boolean },
        ) =>
          pipeState(
            state,
            next => (payload.replace ? adapter.resetAll(next) : next),
            next =>
              adapter.markAsPending(next, {
                scope: payload.scope || DEFAULT_SCOPE_NAME,
                entities: [],
              }),
          ),
        fetchAttachmentsByOfferSuccess: (
          state,
          payload: { data: Attachment[]; scope?: string; replace?: boolean },
        ) =>
          pipeState(
            state,
            next => (payload.replace ? adapter.setMany(next, [], { scope: payload.scope }) : next),
            next => adapter.addMany(next, payload.data, { scope: payload.scope }),
            next =>
              adapter.markAsLoaded(next, {
                scope: payload.scope || DEFAULT_SCOPE_NAME,
                entities: payload.data.map(e => e.id),
              }),
          ),
        fetchAttachmentsByOfferFailed: (state, payload: { error: HttpErrorResponse }) =>
          adapter.setError(state, payload.error),
        fetchAttachmentsByTalentPool: (
          state,
          payload: { params: { offerId: number }; scope?: string; replace?: boolean },
        ) =>
          pipeState(
            state,
            next => (payload.replace ? adapter.resetAll(next) : next),
            next =>
              adapter.markAsPending(next, {
                scope: payload.scope || DEFAULT_SCOPE_NAME,
                entities: [],
              }),
          ),
        fetchAttachmentsByTalentPoolSuccess: (
          state,
          payload: { data: Attachment[]; scope?: string; replace?: boolean },
        ) =>
          pipeState(
            state,
            next => (payload.replace ? adapter.setMany(next, [], { scope: payload.scope }) : next),
            next => adapter.addMany(next, payload.data, { scope: payload.scope }),
            next =>
              adapter.markAsLoaded(next, {
                scope: payload.scope || DEFAULT_SCOPE_NAME,
                entities: payload.data.map(e => e.id),
              }),
          ),
        fetchAttachmentsByTalentPoolFailed: (state, payload: { error: HttpErrorResponse }) =>
          adapter.setError(state, payload.error),
        fetchAttachmentsByRequisition: (
          state,
          payload: { params: { requisitionId: number }; scope?: string; replace?: boolean },
        ) =>
          pipeState(
            state,
            next => (payload.replace ? adapter.resetAll(next) : next),
            next =>
              adapter.markAsPending(next, {
                scope: payload.scope || DEFAULT_SCOPE_NAME,
                entities: [],
              }),
          ),
        fetchAttachmentsByRequisitionSuccess: (
          state,
          payload: { data: Attachment[]; scope?: string; replace?: boolean },
        ) =>
          pipeState(
            state,
            next => (payload.replace ? adapter.setMany(next, [], { scope: payload.scope }) : next),
            next => adapter.addMany(next, payload.data, { scope: payload.scope }),
            next =>
              adapter.markAsLoaded(next, {
                scope: payload.scope || DEFAULT_SCOPE_NAME,
                entities: payload.data.map(e => e.id),
              }),
          ),
        fetchAttachmentsByRequisitionFailed: (state, payload: { error: HttpErrorResponse }) =>
          adapter.setError(state, payload.error),
        deleteAttachment: (state, payload: Attachment['id']) => adapter.markAsPending(state),
        deleteAttachmentSuccess: (state, payload: { data: Attachment }) =>
          pipeState(adapter.removeOne(state, payload.data.id), next => adapter.markAsLoaded(next)),
        deleteAttachmentFailed: (state, payload: HttpErrorResponse) =>
          adapter.setError(state, payload, { entities: [], scope: null }),
        deleteManyAttachments: (state, payload: Array<Attachment['id']>) =>
          adapter.markAsPending(state),
        deleteManyAttachmentsSuccess: (state, payload: { data: Attachment[] }) =>
          pipeState(
            adapter.removeMany(
              state,
              payload.data.map(attachment => attachment.id),
            ),
            next => adapter.markAsLoaded(next),
          ),
        deleteManyAttachmentsFailed: (state, payload: HttpErrorResponse) =>
          adapter.setError(state, payload, { entities: [], scope: null }),
        uploadAttachment: (
          state,
          payload: { file: File; data?: { key: string; value: string | number }; scope?: string },
        ) => adapter.markAsPending(state),
        uploadAttachmentSuccess: state => adapter.markAsLoaded(state),
        uploadAttachmentFailed: (state, payload: HttpErrorResponse) =>
          adapter.setError(state, payload, { entities: [], scope: null }),
        uploadAttachmentPessimistic: (
          state,
          payload: {
            file: File;
            correlationId: string;
            data?: { key: string; value: string | number };
          },
        ) => adapter.markAsPending(state),
        uploadAttachmentPessimisticSuccess: actions.createOnePessimisticSuccess,
        uploadAttachmentPessimisticFailed: actions.createOnePessimisticFailed,
        setAsCv: (state, payload: { candidateId: number; attachmentId: number; parse: boolean }) =>
          adapter.markAsPending(state),
        setAsCvSuccess: state => adapter.markAsLoaded(state),
        setAsCvFailed: (state, payload: HttpErrorResponse) =>
          adapter.setError(state, payload, { entities: [], scope: null }),
        promptSetAsCoverLetter: ngrxEvent<{
          candidateId: number;
          attachmentId: number;
          fileName: string;
        }>(),
        setAsCoverLetter: (
          state,
          payload: { candidateId: number; attachmentId: number; fileName: string },
        ) =>
          adapter.markAsPending(state, {
            scope: getAttachmentStoreScope(AttachmentsDataScope.Candidate, payload.candidateId),
            entities: [payload.attachmentId],
          }),
        setAsCoverLetterSuccess: (state, payload: { attachmentId: number; candidateId: number }) =>
          adapter.markAsLoaded(state, {
            scope: getAttachmentStoreScope(AttachmentsDataScope.Candidate, payload.candidateId),
            entities: [payload.attachmentId],
          }),
        setAsCoverLetterFailed: (state, payload: HttpErrorResponse) =>
          adapter.setError(state, payload, { entities: [], scope: null }),
      };
    },
  },
);

export {
  actions as AttachmentsActions,
  selectors as AttachmentsSelectors,
  reducer as AttachmentsReducer,
  namespace as AttachmentsNamespace,
};
